import { useEffect, useState, type FormEvent } from 'react';
import { type Categoria, type ModoPago } from '../api';
import { Cabecera, AvisoError } from '../componentes';
import { conCache } from '../offline/cache';
import { encolarGasto } from '../offline/cola';
import { api } from '../api';

const HOY = () => new Date().toISOString().slice(0, 10);

export function NuevoGasto({
  notaRef,
  modoPagoNota,
  alGuardar,
  alVolver,
}: {
  notaRef: string | number;
  /** Si la nota ya tiene líneas, su modo manda: mezclarlos partiría el informe. */
  modoPagoNota: ModoPago | null;
  /** Recibe la referencia del gasto para poder ir directo a hacerle la foto. */
  alGuardar: (gastoRef: string, resumen: { concepto: string; importe: number; categoria: string }) => void;
  alVolver: () => void;
}) {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [concepto, setConcepto] = useState('');
  const [importe, setImporte] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [fecha, setFecha] = useState(HOY);
  /**
   * Quién paga ya no se pregunta.
   *
   * Gextia distingue entre adelantarlo tú y pagarlo la empresa, y la diferencia
   * es real —determina si te reembolsan—, pero en FactorLibre los 33 gastos
   * registrados son «lo pago yo»: el otro modo no lo ha usado nadie nunca. Un
   * selector que siempre se responde igual es un paso de más en la pantalla que
   * se rellena con un ticket en la mano.
   *
   * El modo sigue viajando al backend, con el valor que la nota ya tenga, para
   * no romper las notas existentes ni el reparto en dos informes al mezclarlos.
   * Si algún día aparecen tarjetas de empresa, el selector vuelve aquí.
   */
  const modoPago: ModoPago = modoPagoNota ?? 'own_account';
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    // Cacheadas: sin ellas no se puede dar de alta un gasto, y sin cobertura la
    // petición fallaría justo cuando más falta hace.
    conCache('categorias', () => api.categorias())
      .then((resultado) => setCategorias(resultado.datos))
      .catch(setError);
  }, []);

  // Coma o punto: en el móvil el teclado numérico da lo que da según el idioma.
  const importeNumero = Number(importe.replace(',', '.'));
  const importeValido = Number.isFinite(importeNumero) && importeNumero > 0;
  const puedeGuardar = concepto.trim() !== '' && importeValido && categoriaId !== '';

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const categoria = categorias.find((c) => c.id === Number(categoriaId));
      // Se encola siempre. Guardar un gasto no puede depender de la cobertura:
      // es justo lo que la app viene a arreglar.
      const ref = await encolarGasto(notaRef, {
        concepto: concepto.trim(),
        importe: importeNumero,
        categoriaId: Number(categoriaId),
        categoriaNombre: categoria?.name ?? '',
        fecha,
        modoPago,
      });
      alGuardar(ref, {
        concepto: concepto.trim(),
        importe: importeNumero,
        categoria: categoria?.name ?? '',
      });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Cabecera titulo="Nuevo gasto" alVolver={alVolver} />

      <form
        onSubmit={guardar}
        style={{ display: 'contents' }}
        id="form-gasto"
      >
        <div className="contenido">
          <div className="campo importe-campo">
            <label htmlFor="importe">Importe del ticket</label>
            <input
              id="importe"
              // "decimal" da el teclado numérico con separador, sin el pad de
              // teléfono que sale con type="number" en algunos Android.
              inputMode="decimal"
              value={importe}
              onChange={(e) => setImporte(e.target.value)}
              placeholder="0,00"
              autoFocus
              required
            />
            {importe !== '' && !importeValido && (
              <span className="ayuda" style={{ color: 'var(--rojo)' }}>
                Escribe un importe mayor que cero.
              </span>
            )}
          </div>

          <div className="campo">
            <label htmlFor="concepto">Concepto</label>
            <input
              id="concepto"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Comida con el cliente"
              required
            />
          </div>

          <div className="campo">
            <label htmlFor="categoria">Categoría</label>
            <select
              id="categoria"
              value={categoriaId}
              onChange={(e) => setCategoriaId(e.target.value)}
              required
            >
              <option value="">Elige una…</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.name}
                </option>
              ))}
            </select>
            <span className="ayuda">Gextia no acepta el gasto sin categoría.</span>
          </div>

          <div className="campo">
            <label htmlFor="fecha">Fecha</label>
            <input
              id="fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              max={HOY()}
              required
            />
          </div>

          <AvisoError error={error} />
        </div>

        <div className="acciones-abajo">
          <button
            type="submit"
            form="form-gasto"
            className="boton ancho"
            disabled={enviando || !puedeGuardar}
          >
            {enviando ? 'Guardando…' : 'Guardar y hacer foto'}
          </button>
        </div>
      </form>
    </>
  );
}
